/**
 * Lightweight in-memory query cache.
 * Replaces @tanstack/react-query's QueryClient for our use case.
 */

type CacheEntry<T> = {
  data: T;
  timestamp: number;
};

type Listener = () => void;

const STALE_TIME_MS = 5 * 60 * 1000; // 5 minutes

class QueryCache {
  private cache = new Map<string, CacheEntry<unknown>>();
  private listeners = new Map<string, Set<Listener>>();

  private key(queryKey: unknown[]): string {
    return JSON.stringify(queryKey);
  }

  get<T>(queryKey: unknown[]): T | undefined {
    const entry = this.cache.get(this.key(queryKey)) as CacheEntry<T> | undefined;
    if (!entry) return undefined;
    if (Date.now() - entry.timestamp > STALE_TIME_MS) return undefined;
    return entry.data;
  }

  set<T>(queryKey: unknown[], data: T): void {
    this.cache.set(this.key(queryKey), { data, timestamp: Date.now() });
    this.notify(queryKey);
  }

  invalidate(queryKey: unknown[]): void {
    const keyStr = this.key(queryKey);
    // Invalidate all entries whose key starts with any prefix of the given key
    for (const [k] of this.cache) {
      if (k.startsWith(keyStr.slice(0, -1))) {
        this.cache.delete(k);
      }
    }
    // Also invalidate by prefix matching for nested keys like ["admin"]
    const prefix = JSON.stringify(queryKey[0]);
    for (const [k] of this.cache) {
      if (k.startsWith("[" + prefix)) {
        this.cache.delete(k);
      }
    }
    this.notify(queryKey);
  }

  subscribe(queryKey: unknown[], listener: Listener): () => void {
    const key = this.key(queryKey);
    if (!this.listeners.has(key)) {
      this.listeners.set(key, new Set());
    }
    this.listeners.get(key)!.add(listener);
    return () => {
      this.listeners.get(key)?.delete(listener);
    };
  }

  private notify(queryKey: unknown[]): void {
    const key = this.key(queryKey);
    this.listeners.get(key)?.forEach((fn) => fn());
    // Also notify parent keys
    if (queryKey.length > 1) {
      this.notify(queryKey.slice(0, 1));
    }
  }

  clear(): void {
    this.cache.clear();
    this.listeners.forEach((set) => set.forEach((fn) => fn()));
  }
}

export const queryCache = new QueryCache();
